import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, Mail, Phone as PhoneIcon, Calendar, Clock,
  Lock, LogOut, Plus, Trash2, Eye, EyeOff, MessageSquareQuote,
} from "lucide-react";
import {
  api, getAdminPassword, setAdminPassword, clearAdminPassword,
} from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const SERVICE_LABEL = {
  clarity_call: "Clarity Call",
  happiness_coaching_6day: "6-Day Coaching",
  womens_listening: "Women's Listening",
  time_management_60: "Time & Productivity",
  forgiveness_60: "Gift of Forgiveness",
};
const STATUSES = ["new", "contacted", "confirmed", "completed", "cancelled"];

export default function Admin() {
  const [authed, setAuthed] = useState(Boolean(getAdminPassword()));
  if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;
  return <AdminDashboard onLogout={() => setAuthed(false)} />;
}

function AdminLogin({ onSuccess }) {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pwd) return;
    setLoading(true);
    try {
      await api.post("/admin/login", { password: pwd });
      setAdminPassword(pwd);
      toast.success("Welcome back, Dr. Vrinda.");
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sunlit-ivory flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-sunlit-mute hover:text-sunlit-ink mb-8" data-testid="admin-login-back">
          <ArrowLeft className="h-4 w-4" /> Back to site
        </Link>
        <div className="rounded-3xl bg-sunlit-cream border border-stone-200/70 p-8 sm:p-10 shadow-sm">
          <div className="h-12 w-12 rounded-2xl bg-sunlit-gold/20 flex items-center justify-center mb-5">
            <Lock className="h-5 w-5 text-sunlit-gold" />
          </div>
          <h1 className="font-serif-display text-3xl text-sunlit-ink leading-tight">
            Welcome back, <span className="italic text-sunlit-gold">Dr. Vrinda</span>.
          </h1>
          <p className="mt-2 text-sm text-sunlit-mute">Enter your private password to see today's bookings.</p>
          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="admin-login-form">
            <input
              type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
              placeholder="Password" autoFocus
              className="w-full bg-sunlit-ivory border border-stone-200 rounded-xl px-4 py-3 outline-none focus:border-sunlit-gold focus:ring-2 focus:ring-sunlit-gold/30 transition"
              data-testid="admin-login-input"
            />
            <button
              type="submit" disabled={loading}
              className="w-full inline-flex items-center justify-center rounded-full bg-sunlit-gold hover:bg-sunlit-gold-hover disabled:opacity-60 text-sunlit-ink px-6 py-3 text-sm font-medium tracking-wide transition-all shadow-sm hover:shadow-md"
              data-testid="admin-login-submit"
            >
              {loading ? "Opening..." : "Open dashboard"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function AdminDashboard({ onLogout }) {
  const logout = () => {
    clearAdminPassword();
    toast.success("Logged out.");
    onLogout();
  };

  return (
    <div className="min-h-screen bg-sunlit-ivory text-sunlit-ink" data-testid="admin-page">
      <header className="sticky top-0 z-30 bg-sunlit-ivory/90 backdrop-blur-md border-b border-stone-200/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-sunlit-mute hover:text-sunlit-ink" data-testid="admin-back-home">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </Link>
          <p className="font-serif-display text-xl text-sunlit-ink">
            Dr. Vrinda's <span className="italic text-sunlit-gold">dashboard</span>
          </p>
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-4 py-2 text-xs font-medium hover:bg-sunlit-cream transition"
            data-testid="admin-logout-button"
          >
            <LogOut className="h-3.5 w-3.5" /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Tabs defaultValue="bookings" className="w-full" data-testid="admin-tabs">
          <TabsList className="bg-sunlit-cream border border-stone-200/70 h-auto p-1.5 rounded-full">
            <TabsTrigger value="bookings" className="rounded-full px-5 py-2 data-[state=active]:bg-sunlit-gold data-[state=active]:text-sunlit-ink" data-testid="admin-tab-bookings">
              Bookings
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="rounded-full px-5 py-2 data-[state=active]:bg-sunlit-gold data-[state=active]:text-sunlit-ink" data-testid="admin-tab-testimonials">
              Testimonials
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-8" data-testid="admin-tab-content-bookings">
            <BookingsPanel onUnauthorized={() => { clearAdminPassword(); onLogout(); }} />
          </TabsContent>

          <TabsContent value="testimonials" className="mt-8" data-testid="admin-tab-content-testimonials">
            <TestimonialsPanel onUnauthorized={() => { clearAdminPassword(); onLogout(); }} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function BookingsPanel({ onUnauthorized }) {
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([
        api.get("/bookings"),
        api.get("/bookings/stats"),
      ]);
      setBookings(b.data);
      setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        onUnauthorized();
        return;
      }
      toast.error("Could not load bookings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status });
      toast.success(`Marked as ${status}.`);
      load();
    } catch (e) {
      toast.error("Update failed.");
    }
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <button onClick={load} className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-4 py-2 text-xs font-medium hover:bg-sunlit-cream transition" data-testid="admin-refresh-button">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="New" value={stats.new} accent />
          <StatCard label="Confirmed" value={stats.confirmed} />
          <StatCard label="Completed" value={stats.completed} />
        </div>
      )}

      <div className="space-y-4" data-testid="admin-bookings-list">
        {bookings.length === 0 && !loading && (
          <div className="rounded-3xl bg-sunlit-cream border border-stone-200/70 p-10 text-center text-sunlit-mute" data-testid="admin-empty">
            No bookings yet. They'll appear here as soon as anyone submits the form.
          </div>
        )}

        {bookings.map((b) => (
          <article key={b.id} className="rounded-3xl bg-sunlit-cream border border-stone-200/70 p-6 sm:p-7" data-testid={`admin-booking-${b.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="font-serif-display text-2xl text-sunlit-ink">{b.name}</p>
                <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-sunlit-mute">
                  <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{b.email}</span>
                  <span className="inline-flex items-center gap-1.5"><PhoneIcon className="h-3.5 w-3.5" />{b.phone}</span>
                  {b.preferred_date && <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{b.preferred_date}</span>}
                  {b.preferred_time && <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{b.preferred_time}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-sunlit-ivory border border-stone-200 px-3 py-1 text-xs text-sunlit-mute">
                  {SERVICE_LABEL[b.service_type] ?? b.service_type}
                </span>
                <StatusBadge status={b.status} />
              </div>
            </div>
            {b.message && (
              <p className="mt-4 text-sm text-sunlit-mute leading-relaxed border-l-2 border-sunlit-gold/60 pl-4">{b.message}</p>
            )}
            <div className="mt-5 flex flex-wrap gap-2">
              {STATUSES.filter((s) => s !== b.status).map((s) => (
                <button key={s} onClick={() => updateStatus(b.id, s)} className="rounded-full border border-stone-300 px-3 py-1.5 text-xs font-medium text-sunlit-ink hover:bg-sunlit-ivory transition" data-testid={`admin-set-${s}-${b.id}`}>
                  Mark {s}
                </button>
              ))}
            </div>
            <p className="mt-4 text-[10px] uppercase tracking-widest text-sunlit-mute/70">
              Submitted: {new Date(b.created_at).toLocaleString()}
            </p>
          </article>
        ))}
      </div>
    </>
  );
}

function TestimonialsPanel({ onUnauthorized }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ quote: "", name: "", role: "", sort_order: 0 });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/testimonials");
      setItems(r.data);
    } catch (e) {
      if (e?.response?.status === 401) { onUnauthorized(); return; }
      toast.error("Could not load testimonials.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.quote.trim() || !form.name.trim()) {
      toast.error("Quote and name are required.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/testimonials", {
        ...form,
        sort_order: Number(form.sort_order) || 0,
        published: true,
      });
      toast.success("Testimonial added.");
      setForm({ quote: "", name: "", role: "", sort_order: 0 });
      load();
    } catch (e) {
      toast.error("Could not add.");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (t) => {
    try {
      await api.patch(`/admin/testimonials/${t.id}`, { published: !t.published });
      load();
    } catch (e) { toast.error("Update failed."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await api.delete(`/admin/testimonials/${id}`);
      toast.success("Deleted.");
      load();
    } catch (e) { toast.error("Delete failed."); }
  };

  return (
    <>
      <div className="rounded-3xl bg-sunlit-cream border border-stone-200/70 p-6 sm:p-7 mb-8" data-testid="admin-testimonial-add-form">
        <p className="font-serif-display text-xl text-sunlit-ink flex items-center gap-2">
          <Plus className="h-4 w-4 text-sunlit-gold" /> Add a new testimonial
        </p>
        <form onSubmit={add} className="mt-5 grid sm:grid-cols-2 gap-4">
          <label className="sm:col-span-2 block">
            <span className="block text-xs uppercase tracking-wider text-sunlit-mute mb-2">Quote *</span>
            <textarea value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} rows={4}
              placeholder="What they said, in their own words..."
              className="w-full bg-sunlit-ivory border border-stone-200 rounded-xl px-4 py-3 outline-none focus:border-sunlit-gold focus:ring-2 focus:ring-sunlit-gold/30 transition resize-none"
              data-testid="admin-testimonial-quote" />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-sunlit-mute mb-2">Name *</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Anjali R."
              className="w-full bg-sunlit-ivory border border-stone-200 rounded-xl px-4 py-3 outline-none focus:border-sunlit-gold focus:ring-2 focus:ring-sunlit-gold/30 transition"
              data-testid="admin-testimonial-name" />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-sunlit-mute mb-2">Place / role</span>
            <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              placeholder="Trivandrum"
              className="w-full bg-sunlit-ivory border border-stone-200 rounded-xl px-4 py-3 outline-none focus:border-sunlit-gold focus:ring-2 focus:ring-sunlit-gold/30 transition"
              data-testid="admin-testimonial-role" />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-wider text-sunlit-mute mb-2">Sort order</span>
            <input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
              className="w-full bg-sunlit-ivory border border-stone-200 rounded-xl px-4 py-3 outline-none focus:border-sunlit-gold focus:ring-2 focus:ring-sunlit-gold/30 transition"
              data-testid="admin-testimonial-sort" />
          </label>
          <div className="sm:col-span-2">
            <button type="submit" disabled={saving}
              className="inline-flex items-center gap-2 rounded-full bg-sunlit-gold hover:bg-sunlit-gold-hover disabled:opacity-60 text-sunlit-ink px-6 py-3 text-sm font-medium tracking-wide transition-all shadow-sm hover:shadow-md"
              data-testid="admin-testimonial-add-submit">
              {saving ? "Adding..." : "Add testimonial"}
            </button>
          </div>
        </form>
      </div>

      <div className="space-y-4" data-testid="admin-testimonials-list">
        {!loading && items.length === 0 && (
          <div className="rounded-3xl bg-sunlit-cream border border-stone-200/70 p-10 text-center text-sunlit-mute">
            No testimonials yet.
          </div>
        )}
        {items.map((t) => (
          <article key={t.id} className={`rounded-3xl border p-6 sm:p-7 ${t.published ? "bg-sunlit-cream border-stone-200/70" : "bg-stone-100 border-stone-300/70 opacity-80"}`} data-testid={`admin-testimonial-${t.id}`}>
            <div className="flex items-start justify-between gap-4 mb-3">
              <div>
                <p className="font-serif-display text-xl text-sunlit-ink">{t.name}</p>
                {t.role && <p className="text-xs text-sunlit-mute mt-0.5">{t.role}</p>}
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-sunlit-ivory border border-stone-200 px-3 py-1 text-[10px] uppercase tracking-widest text-sunlit-mute">
                  Order: {t.sort_order}
                </span>
                <span className={`rounded-full px-3 py-1 text-xs font-medium ${t.published ? "bg-sunlit-gold/25 text-sunlit-ink" : "bg-stone-300 text-stone-700"}`}>
                  {t.published ? "Live" : "Hidden"}
                </span>
              </div>
            </div>
            <p className="text-sm text-sunlit-mute leading-relaxed border-l-2 border-sunlit-gold/60 pl-4 italic">
              "{t.quote}"
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <button onClick={() => togglePublish(t)} className="inline-flex items-center gap-1.5 rounded-full border border-stone-300 px-3 py-1.5 text-xs font-medium text-sunlit-ink hover:bg-sunlit-ivory transition" data-testid={`admin-testimonial-toggle-${t.id}`}>
                {t.published ? <><EyeOff className="h-3 w-3" /> Hide</> : <><Eye className="h-3 w-3" /> Publish</>}
              </button>
              <button onClick={() => remove(t.id)} className="inline-flex items-center gap-1.5 rounded-full border border-red-200 text-red-700 hover:bg-red-50 px-3 py-1.5 text-xs font-medium transition" data-testid={`admin-testimonial-delete-${t.id}`}>
                <Trash2 className="h-3 w-3" /> Delete
              </button>
            </div>
          </article>
        ))}
      </div>
    </>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className={`rounded-3xl p-6 border ${accent ? "bg-sunlit-gold/15 border-sunlit-gold/40" : "bg-sunlit-cream border-stone-200/70"}`} data-testid={`admin-stat-${label.toLowerCase()}`}>
      <p className="text-xs uppercase tracking-widest text-sunlit-mute">{label}</p>
      <p className="mt-2 font-serif-display text-4xl text-sunlit-ink">{value}</p>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    new: "bg-sunlit-gold/25 text-sunlit-ink",
    contacted: "bg-blue-100 text-blue-800",
    confirmed: "bg-green-100 text-green-800",
    completed: "bg-stone-200 text-stone-700",
    cancelled: "bg-red-100 text-red-700",
  };
  return <span className={`rounded-full px-3 py-1 text-xs font-medium capitalize ${map[status] ?? "bg-stone-200"}`}>{status}</span>;
}
